import React, { useMemo } from "react";
import { MainSurveyStore } from "modules/MainSurveyEditor/hooks/useMainSurvey";
import { ListOfInputsSection, ListOfInputs, ListOfInputsInput } from "shared/components/Inputs/ListOfInputs";
import { Typography } from "@mui/material";
import { ConditionActionTypes } from "../helpers/conditions";
import { ConditionInput } from "./ConditionInput";
import { Conditions as ConditionsTypes } from "../conditions";

export interface ConditionsController {
  conditions: Partial<ConditionsTypes.Condition>[];
  addCondition: (actionType: ConditionActionTypes) => void;
  deleteCondition: (index: number) => void;
  onUpdate: (index: number, update: Partial<ConditionsTypes.Condition>) => void;
  selectPath: string;
  setSelectPath: (path: string) => void;
  selectGoBackAction?: { onClick: () => void; title: string };
}

interface ConditionsViewProps {
  mainSurveyStore: MainSurveyStore;
  controller: ConditionsController;
  extraNote?: string;
}

export const ConditionsView = (props: ConditionsViewProps) => {
  const { mainSurveyStore, controller, extraNote } = props;
  const { conditions, addCondition, deleteCondition, onUpdate, selectPath, setSelectPath, selectGoBackAction } =
    controller;

  const inputSections: ListOfInputsSection[] = useMemo(() => {
    const visibilityConditions: ListOfInputsInput[] = [];
    const lockingConditions: ListOfInputsInput[] = [];

    conditions?.forEach((condition, i) => {
      const input = {
        inputElement: (
          <ConditionInput
            mainSurveyStore={mainSurveyStore}
            condition={condition}
            onUpdate={(update: Partial<ConditionsTypes.Condition>) => onUpdate(i, update)}
            selectPath={selectPath}
            setSelectPath={setSelectPath}
            valueOptions={condition.contentObject?.options || []}
            selectGoBackAction={selectGoBackAction}
          />
        ),
        onDelete: () => deleteCondition(i),
      };

      if (condition.actionType === ConditionActionTypes.Hide) {
        visibilityConditions.push(input);
      } else {
        lockingConditions.push(input);
      }
    });

    return [
      {
        title: "Visibility",
        subTitle: <Typography mb={2}>Only hide when all conditions are met</Typography>,
        addAction: { title: "Add Condition", onClick: () => addCondition(ConditionActionTypes.Hide) },
        inputs: visibilityConditions,
      },
      {
        title: "Locking",
        subTitle: <Typography mb={2}>Only lock when all conditions are met</Typography>,
        addAction: { title: "Add Condition", onClick: () => addCondition(ConditionActionTypes.Lock) },
        inputs: lockingConditions,
      },
    ];
  }, [conditions, mainSurveyStore, selectPath, setSelectPath, onUpdate, deleteCondition, addCondition, selectGoBackAction]);

  return (
    <>
      {extraNote && (
        <Typography variant="body2" color="text.tertiary" mb={1}>
          {extraNote}
        </Typography>
      )}
      <ListOfInputs
        title="Conditions"
        inputSections={inputSections}
        addOptions={[
          { title: "Add visibility condition", onClick: () => addCondition(ConditionActionTypes.Hide) },
          { title: "Add locking condition", onClick: () => addCondition(ConditionActionTypes.Lock) },
        ]}
      />
    </>
  );
};
